import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Box, Fade } from '@mui/material';

interface ProgressFactsProps {
  type: 'simulation' | 'ideaGen';
}

// Educational facts for each process type
const facts = {
  simulation: [
    "WaveScoop analyzes patterns from millions of social interactions to predict responses",
    "Each simulation generates unique personas based on your target audience demographics",
    "Our AI adapts predictions to your specific industry sector and brand voice",
    "Forecasts include sentiment analysis, engagement metrics, and reputation impact scores",
    "Simulations learn from real-world trends and emerging cultural conversations",
    "Every prediction is tailored to your content's context and intended audience"
  ],
  ideaGen: [
    "Ideas are crafted based on current trends and your audience's preferences",
    "Content suggestions are optimized for your chosen platform's algorithm",
    "Suggestions are personalized to match your brand's unique voice and tone",
    "Our AI analyzes successful content patterns specific to your industry",
    "Ideas incorporate emerging topics before they become oversaturated",
    "Content recommendations balance creativity with proven engagement strategies"
  ]
};

const ProgressFacts: React.FC<ProgressFactsProps> = ({ type }) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const factList = facts[type];
  
  // Fade timeout configuration
  const fadeTimeout = 300;
  
  const nextFact = useCallback(() => {
    if (isTransitioning) return; // Prevent overlapping transitions
    
    setIsTransitioning(true);
    setIsVisible(false);
  }, [isTransitioning]);
  
  const handleExited = useCallback(() => {
    // Change fact after fade-out completes
    setCurrentFactIndex((prev) => (prev + 1) % factList.length);
    
    // Use requestAnimationFrame to ensure DOM update before showing new content
    requestAnimationFrame(() => {
      setIsVisible(true);
      setIsTransitioning(false);
    });
  }, [factList.length]);
  
  useEffect(() => {
    // Rotate through facts every 6 seconds
    const interval = setInterval(nextFact, 6000);
    return () => clearInterval(interval);
  }, [nextFact]);
  
  return (
    <Box
      sx={{
        minHeight: { xs: '3.5rem', sm: '3rem' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 4 },
        mb: 2,
        // Safari-specific rendering fixes
        WebkitBackfaceVisibility: 'hidden',
        WebkitTransform: 'translateZ(0)',
        transform: 'translateZ(0)',
      }}
    >
      <Fade 
        in={isVisible} 
        timeout={fadeTimeout}
        onExited={handleExited}
        unmountOnExit={false} // Keep mounted to prevent flashing
      >
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
            lineHeight: 1.5,
            fontStyle: 'italic'
          }}
        >
          {factList[currentFactIndex]}
        </Typography>
      </Fade>
    </Box>
  );
};

export default ProgressFacts;